import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { usePublicMeta } from "@/lib/publicMeta";
import { useAuth } from "@/context/AuthContext";
import PublicHome from "@/pages/PublicHome";
import Shop from "@/pages/Shop";
import { api } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Spinner } from "@/components/Bits";

/**
 * Where "/" and any unknown path go. The owner starts at their platform
 * overview; client staff go straight to their own inventory dashboard.
 */
export default function Landing() {
  const { user, loading } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant();
  const meta = usePublicMeta();
  // A visitor on a client's own address wants their shop, not our sign-in page.
  // Whether this address belongs to a client is a property of the address, so
  // it's worth remembering. The shop's contents are not — remembering those
  // served customers stale prices and stock until they closed the tab.
  const HOST_KEY = `shop-host:${window.location.host}`;
  const [isShopHost, setIsShopHost] = useState(() => {
    try {
      const saved = sessionStorage.getItem(HOST_KEY);
      return saved === null ? null : saved === "true";
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (isShopHost !== null) return;
    let cancelled = false;

    // Only a 404 is a real answer: nobody owns this address. A timeout or a
    // server still waking up says nothing, and treating it as "not a shop"
    // sent a client's customers to our own home page — and remembered it for
    // the rest of the tab.
    const ask = async (attempt = 0) => {
      try {
        await api.get("/shop-by-host");
        if (!cancelled) remember(true);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          remember(false);
          return;
        }
        if (attempt < 2) {
          setTimeout(() => ask(attempt + 1), 600 * (attempt + 1));
          return;
        }
        // Still no answer. Carry on as our own site, but don't remember it —
        // the next load asks again.
        setIsShopHost(false);
      }
    };

    ask();
    return () => {
      cancelled = true;
    };

    function remember(value) {
      setIsShopHost(value);
      try {
        sessionStorage.setItem(HOST_KEY, String(value));
      } catch {
        /* private browsing — just means asking again next time */
      }
    }
  }, [isShopHost, HOST_KEY]);

  // Decided before anything to do with signing in: a customer on a client's
  // own address has no account here and shouldn't wait on an auth check, or
  // see a blank page while one runs.
  if (isShopHost) return <Shop />;

  if (isShopHost === null || loading || user === null || tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <Spinner className="w-6 h-6" />
      </div>
    );
  }

  if (!user) {
    return meta.public?.landing?.enabled ? <PublicHome /> : <Navigate to="/login" replace />;
  }
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  if (user.role === "owner") return <Navigate to="/overview" replace />;
  if (!tenantId) return <Navigate to="/login" replace />;
  return <Navigate to="/dashboard" replace />;
}
